import { useEffect, useState, useCallback } from "react";
import { Download, X, Share, SquarePlus, Smartphone } from "lucide-react";
import { Logo, Brand } from "@/components/Logo";

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export const isIOSDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);

// Sidebar/other components call this to open the installer on demand.
export const openInstall = () => window.dispatchEvent(new Event("aiblack:open-install"));
// ChatView fires this after real usage so the auto-banner feels earned.
export const markActivity = () => window.dispatchEvent(new Event("aiblack:activity"));

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const installed = isStandalone();
  const ios = isIOSDevice() && !installed;

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onOpen = () => setShowModal(true);
    const onActivity = () => {
      if (installed) return;
      if (localStorage.getItem("aiblack_install_dismissed")) return;
      // Show the earned banner only for platforms that can actually install.
      if (isIOSDevice() || window.__aiblackDeferred) setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("aiblack:open-install", onOpen);
    window.addEventListener("aiblack:activity", onActivity);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("aiblack:open-install", onOpen);
      window.removeEventListener("aiblack:activity", onActivity);
    };
  }, [installed]);

  // keep a global ref so the activity handler can check availability synchronously
  useEffect(() => {
    window.__aiblackDeferred = !!deferred;
  }, [deferred]);

  const nativeInstall = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setShowBanner(false);
    setShowModal(false);
  }, [deferred]);

  const dismissBanner = () => {
    localStorage.setItem("aiblack_install_dismissed", "1");
    setShowBanner(false);
  };

  const IOSSteps = () => (
    <ol className="space-y-3 mt-1" data-testid="ios-install-steps">
      <li className="flex items-center gap-3 text-sm text-[#c7ccd6]">
        <span className="w-6 h-6 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] font-bold text-xs flex items-center justify-center shrink-0">1</span>
        Tap the <Share size={16} className="text-[#FF5A00] inline mx-0.5" /> <span className="font-semibold text-white">Share</span> button in Safari's toolbar
      </li>
      <li className="flex items-center gap-3 text-sm text-[#c7ccd6]">
        <span className="w-6 h-6 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] font-bold text-xs flex items-center justify-center shrink-0">2</span>
        Scroll down and tap <SquarePlus size={16} className="text-[#FF5A00] inline mx-0.5" /> <span className="font-semibold text-white">Add to Home Screen</span>
      </li>
      <li className="flex items-center gap-3 text-sm text-[#c7ccd6]">
        <span className="w-6 h-6 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] font-bold text-xs flex items-center justify-center shrink-0">3</span>
        Tap <span className="font-semibold text-white">Add</span> — that's it!
      </li>
    </ol>
  );

  return (
    <>
      {/* Auto banner — appears after the user has used the app a bit */}
      {showBanner && !showModal && (
        <div data-testid="install-prompt"
          className="fixed bottom-4 left-4 right-4 mx-auto z-[60] max-w-md bg-[#15151d] border border-[#FF5A00]/40 rounded-2xl shadow-2xl p-4 fade-up"
          style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
          {ios ? (
            <div>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#0a0a0f] border border-[#2a3040] flex items-center justify-center shrink-0">
                  <Logo size="text-sm" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white">Add <Brand /> to your Home Screen</div>
                  <div className="text-xs text-[#9ca3af]">Instant access to your Unfair Advantage.</div>
                </div>
                <button data-testid="install-dismiss" onClick={dismissBanner} aria-label="Dismiss"
                  className="shrink-0 text-[#6b7280] hover:text-white transition-colors"><X size={18} /></button>
              </div>
              <div className="mt-3 pt-3 border-t border-[#2a3040]"><IOSSteps /></div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-[#0a0a0f] border border-[#2a3040] flex items-center justify-center shrink-0">
                <Logo size="text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">Add <Brand /> to your Home Screen</div>
                <div className="text-xs text-[#9ca3af]">Instant access to your Unfair Advantage.</div>
              </div>
              <button data-testid="install-accept" onClick={nativeInstall}
                className="shrink-0 flex items-center gap-1.5 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-full px-4 py-2 text-sm hover:bg-[#FF7A33] transition-colors">
                <Download size={15} /> Add
              </button>
              <button data-testid="install-dismiss" onClick={dismissBanner} aria-label="Dismiss"
                className="shrink-0 text-[#6b7280] hover:text-white transition-colors"><X size={18} /></button>
            </div>
          )}
        </div>
      )}

      {/* Manual modal — opened from the sidebar "Install app" button */}
      {showModal && (
        <div data-testid="install-modal" className="fixed inset-0 z-[80] flex items-center justify-center px-4"
          onClick={() => setShowModal(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-md bg-[#15151d] border border-[#2a3040] rounded-2xl shadow-2xl p-6 fade-up"
            onClick={(e) => e.stopPropagation()}>
            <button data-testid="install-modal-close" onClick={() => setShowModal(false)} aria-label="Close"
              className="absolute top-4 right-4 text-[#6b7280] hover:text-white transition-colors"><X size={20} /></button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#0a0a0f] border border-[#2a3040] flex items-center justify-center shrink-0">
                <Smartphone size={22} className="text-[#FF5A00]" />
              </div>
              <div>
                <div className="font-display font-black text-xl text-white">Add <Brand /> to your Home Screen</div>
                <div className="text-xs text-[#9ca3af]">Instant access to your Unfair Advantage.</div>
              </div>
            </div>

            {installed ? (
              <div className="text-sm text-[#4ade80]" data-testid="already-installed">
                aiBLACK is already installed on this device. Look for it on your home screen!
              </div>
            ) : ios ? (
              <IOSSteps />
            ) : deferred ? (
              <button data-testid="install-modal-accept" onClick={nativeInstall}
                className="w-full flex items-center justify-center gap-2 bg-[#FF5A00] text-[#0a0a0f] font-bold rounded-lg px-6 py-3.5 hover:bg-[#FF7A33] transition-colors">
                <Download size={18} /> Add to Home Screen
              </button>
            ) : (
              <div className="text-sm text-[#c7ccd6] space-y-2" data-testid="install-generic">
                <p>Install aiBLACK from your browser menu:</p>
                <p className="text-[#9ca3af]">
                  On <span className="text-white font-semibold">Chrome / Edge</span>, open the browser menu (⋮) and choose
                  <span className="text-white font-semibold"> "Install app"</span> or
                  <span className="text-white font-semibold"> "Add to Home Screen"</span>.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
